'use client';

import Image from 'next/image';
import { type MessageKeys, useTranslations } from 'next-intl';
import React, { useEffect } from 'react';
import VideoLazyLoad from '@/app/components/shared/VideoLazyLoad';
import { Button } from '@/app/components/ui/button';
import { HOME_PAGE } from '@/app/constants';
import { cn, reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { FeatureCarousel, type FeatureCarouselItem } from '../../_components/FeatureCarousel';
import ProductInputTool from '../../_components/ProductInputTool';
import { reportProductPageElementClick } from '../../_components/ProductInputTool/utils';
import { QuestionSection } from './QuestionSection';

export const ViduQ3: React.FC = () => {
  const router = useRouter();
  const t = useTranslations('vidu-q3');

  // Hero Section 数据
  const heroFeature = {
    title: t('title'),
    description: t('subtitle'),
    videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/vidu-q3-954a9d4b.mp4',
    mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/vidu-q3-954a9d4b-ebf3b2f2.mp4',
    videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/vidu-q3-bf6299e9.webp',
  };

  // 所有部分的配置
  const sections: {
    id: string;
    titleKey: MessageKeys<IntlMessages, 'features.title'>;
    features: FeatureCarouselItem[];
  }[] = [
    {
      id: 'features',
      titleKey: 'features.title',
      features: [
        {
          id: 'directAudioVideo',
          title: t('features.directAudioVideo.title'),
          description: t('features.directAudioVideo.description'),
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/directAudioVideo-055a0a4f.webp',
          mobilePoster: 'https://image01.cf.vidu.studio/vidu/media-asset/directAudioVideo-055a0a4f-2e4e5f7b.webp',
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/directAudioVideo-21c39cdb.mp4',
          mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/directAudioVideo-21c39cdb-df046b25.mp4',
        },
        {
          id: 'longVideo',
          title: t('features.longVideo.title'),
          description: t('features.longVideo.description'),
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/longVideo-c858768f.webp',
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/longVideo-117e8b6d.mp4',
          mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/longVideo-117e8b6d-e02d0b63.mp4',
        },
        {
          id: 'cameraControl',
          title: t('features.cameraControl.title'),
          description: t('features.cameraControl.description'),
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/cameraControl-e39ed728.webp',
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/cameraControl-34892846.mp4',
          mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/cameraControl-34892846-64f8e225.mp4',
        },
      ],
    },
  ];

  useEffect(() => {
    reportEvent('feature_page_view', {
      from_page: 'vidu q3',
    });
  }, []);

  // 处理生成按钮点击
  const handleGenerateClick = () => {
    reportEvent('element_click', {
      element_name: 'generate-now',
      page: 'vidu-q3',
    });
    reportProductPageElementClick('generate now');
    router.push(HOME_PAGE);
  };

  // Highlights 优势项配置
  const highlights: {
    id: string;
    titleKey: MessageKeys<
      IntlMessages,
      | 'highlights.audioVideoSync.title'
      | 'highlights.multilingual.title'
      | 'highlights.proCreation.title'
      | 'highlights.multiSpeaker.title'
    >;
    icon: string;
    descriptionKey: MessageKeys<
      IntlMessages,
      | 'highlights.audioVideoSync.description'
      | 'highlights.multilingual.description'
      | 'highlights.proCreation.description'
      | 'highlights.multiSpeaker.description'
    >;
  }[] = [
    {
      id: 'audioVideoSync',
      icon: 'https://image01.cf.vidu.studio/vidu/media-asset/audioVideoSync-cb563383.webp',
      titleKey: 'highlights.audioVideoSync.title',
      descriptionKey: 'highlights.audioVideoSync.description',
    },
    {
      id: 'multilingual',
      icon: 'https://image01.cf.vidu.studio/vidu/media-asset/multilingual-fa8958fd.webp',
      titleKey: 'highlights.multilingual.title',
      descriptionKey: 'highlights.multilingual.description',
    },
    {
      id: 'proCreation',
      icon: 'https://image01.cf.vidu.studio/vidu/media-asset/proCreation-0203f624.webp',
      titleKey: 'highlights.proCreation.title',
      descriptionKey: 'highlights.proCreation.description',
    },
    {
      id: 'multiSpeaker',
      icon: 'https://image01.cf.vidu.studio/vidu/media-asset/multiSpeaker-75a71047.webp',
      titleKey: 'highlights.multiSpeaker.title',
      descriptionKey: 'highlights.multiSpeaker.description',
    },
  ];

  return (
    <div className="flex min-h-screen w-full flex-col bg-black text-white">
      {/* Hero Section */}
      <section className="pt-[164px] sm:pt-16">
        <div className="mx-auto flex flex-col items-center px-4">
          <div className="w-full text-center">
            <h1 className="mb-4 font-semibold text-[48px] leading-tight sm:text-[36px]">{heroFeature.title}</h1>
            <p className="mb-12 font-normal text-xl sm:mb-6 sm:text-base">{heroFeature.description}</p>
          </div>
          <div className="mx-auto min-h-[200px] w-full max-w-[50vw] md:max-w-full">
            <ProductInputTool />
          </div>
          {heroFeature.videoSrc && heroFeature.videoSrc !== '' && (
            <div className="mt-12 w-[74vw] overflow-hidden rounded-xl sm:w-full">
              <VideoLazyLoad
                videoSrc={heroFeature.videoSrc}
                mobileVideoSrc={heroFeature.mobileVideoSrc}
                poster={heroFeature.videoPoster}
                priority
              />
            </div>
          )}
        </div>
      </section>

      {/* Feature Sections */}
      {sections.map((section, index) => (
        <FeatureCarousel
          className="pt-40"
          key={section.id}
          title={t('features.title')}
          features={section.features}
          buttonText={t('buttons.generateNow')}
          onButtonClick={handleGenerateClick}
          reverseLayout={index % 2 === 1}
        />
      ))}

      {/* Highlights Section */}
      <section className="w-full bg-black pt-28 sm:pt-6">
        <div className="mx-auto max-w-[1440px] px-8 py-12 text-center md:px-4 md:py-8">
          <div className="mb-16 text-center">
            <h2 className="font-semibold text-[40px] leading-tight sm:text-[36px]">{t('highlights.title')}</h2>
          </div>
          <div className="flex justify-between sm:flex-wrap sm:justify-center sm:gap-y-8">
            {highlights.map((highlight, index) => {
              return (
                <React.Fragment key={highlight.id}>
                  <div className="flex w-[20%] flex-col items-center text-center sm:w-[48%]">
                    <div className="mb-4 h-16 w-20 text-center sm:mb-1">
                      <img
                        className="mx-auto size-16 sm:size-12"
                        src={highlight.icon}
                        alt={t(highlight.titleKey)}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>

                    <div className="mx-auto min-w-[110px] whitespace-pre-line text-center font-medium text-base text-white leading-5 sm:text-xs">
                      {t(highlight.titleKey)}
                    </div>
                    <p className="mt-2 text-center font-normal text-sm text-white/60 sm:text-xs">
                      {t(highlight.descriptionKey)}
                    </p>
                  </div>

                  {index < highlights.length - 1 && (
                    <div className={cn('flex items-center', index === 1 && 'sm:hidden')}>
                      <div className="h-[60px] w-[2px] bg-white/20" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <QuestionSection />

      {/* CTA Banner */}
      <section className="relative mb-[160px] h-[364px] w-full sm:h-[219px]">
        <Image
          src="https://image01.cf.vidu.studio/vidu/media-asset/CTA_Banner-0d6559ab.webp"
          alt="CTA Banner"
          fill
          className="object-cover"
          sizes="100vw"
          loading="lazy"
        />

        {/* 内容覆盖层 */}
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-8">
            <h2 className="mb-10 font-bold text-[48px] sm:font-semibold sm:text-[32px]">{t('cta.title')}</h2>
            <Button
              className="h-[60px] min-w-[224px] rounded-[100px] bg-[#0086FF] px-10 py-5 font-medium text-white text-xl shadow-lg transition-all hover:shadow-xl sm:h-12 sm:w-[195px] sm:py-4 sm:text-base"
              onClick={handleGenerateClick}
            >
              {t('buttons.generateNow')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
